import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'

function MetroMap() {
  const navigate = useNavigate()
  const { stations, lines, connections, selectedCity, currentCity, isLoading } = useMetro()
  const svgRef = useRef<SVGSVGElement>(null)

  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [selectedStation, setSelectedStation] = useState<string | null>(null)
  const [hoveredStation, setHoveredStation] = useState<string | null>(null)

  // Calculate bounds from station positions
  const bounds = stations.reduce(
    (acc, station) => {
      const pos = station.mapPosition || station.coordinates
      if (pos) {
        const x = station.mapPosition?.x ?? (station.coordinates?.longitude || 0) * 100
        const y = station.mapPosition?.y ?? (station.coordinates?.latitude || 0) * -100
        return {
          minX: Math.min(acc.minX, x),
          maxX: Math.max(acc.maxX, x),
          minY: Math.min(acc.minY, y),
          maxY: Math.max(acc.maxY, y),
        }
      }
      return acc
    },
    { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
  )

  const width = bounds.maxX - bounds.minX + 200
  const height = bounds.maxY - bounds.minY + 200
  const offsetX = -bounds.minX + 100
  const offsetY = -bounds.minY + 100

  const getStationPosition = (station: typeof stations[0]) => {
    if (station.mapPosition) {
      return { x: station.mapPosition.x + offsetX, y: station.mapPosition.y + offsetY }
    }
    if (station.coordinates) {
      return {
        x: station.coordinates.longitude * 100 + offsetX,
        y: station.coordinates.latitude * -100 + offsetY,
      }
    }
    return { x: 0, y: 0 }
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setScale(prev => Math.min(Math.max(prev * delta, 0.5), 3))
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const resetView = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }

  const zoomIn = () => setScale(prev => Math.min(prev * 1.2, 3))
  const zoomOut = () => setScale(prev => Math.max(prev / 1.2, 0.5))

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">No City Selected</h2>
            <p className="text-neutral-dark/70 mb-4">Please select a city to view the metro map</p>
            <Link to="/cities" className="btn-primary">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading metro map...</p>
          </div>
        </div>
      </div>
    )
  }

  const selectedStationData = selectedStation ? stations.find(s => s.id === selectedStation) : null

  return (
    <div className="py-4 lg:py-8">
      <div className="container-responsive">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Map Area */}
          <div className="flex-1">
            <div className="card p-2 lg:p-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl lg:text-2xl font-bold">
                  {currentCity?.metroName} Map
                </h1>
                <div className="flex items-center gap-2">
                  <button
                    onClick={zoomOut}
                    className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xl"
                  >
                    −
                  </button>
                  <span className="text-sm text-neutral-dark/70 w-16 text-center">
                    {Math.round(scale * 100)}%
                  </span>
                  <button
                    onClick={zoomIn}
                    className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xl"
                  >
                    +
                  </button>
                  <button
                    onClick={resetView}
                    className="ml-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* SVG Map */}
              <div
                className="relative overflow-hidden bg-neutral-light rounded-lg"
                style={{ height: '60vh', minHeight: '400px' }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <svg
                  ref={svgRef}
                  width="100%"
                  height="100%"
                  viewBox={`0 0 ${width} ${height}`}
                  style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transformOrigin: 'center center',
                  }}
                >
                  {/* Connection Lines */}
                  {connections.map((conn, index) => {
                    const fromStation = stations.find(s => s.id === conn.from)
                    const toStation = stations.find(s => s.id === conn.to)
                    const line = lines.find(l => l.id === conn.line)

                    if (!fromStation || !toStation) return null

                    const from = getStationPosition(fromStation)
                    const to = getStationPosition(toStation)

                    return (
                      <line
                        key={`${conn.from}-${conn.to}-${index}`}
                        x1={from.x}
                        y1={from.y}
                        x2={to.x}
                        y2={to.y}
                        stroke={line?.colorHex || '#999'}
                        strokeWidth={4}
                        strokeLinecap="round"
                      />
                    )
                  })}

                  {/* Station Circles */}
                  {stations.map(station => {
                    const pos = getStationPosition(station)
                    const isSelected = station.id === selectedStation
                    const isHovered = station.id === hoveredStation
                    const stationLines = station.lines.map(lineId => lines.find(l => l.id === lineId))
                    const primaryColor = stationLines[0]?.colorHex || '#4A90D9'

                    return (
                      <g key={station.id}>
                        {/* Station circle */}
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r={station.isInterchange ? 10 : 6}
                          fill={isSelected ? primaryColor : 'white'}
                          stroke={primaryColor}
                          strokeWidth={station.isInterchange ? 4 : 3}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedStation(station.id)}
                          onMouseEnter={() => setHoveredStation(station.id)}
                          onMouseLeave={() => setHoveredStation(null)}
                        />

                        {/* Interchange indicator */}
                        {station.isInterchange && (
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={14}
                            fill="none"
                            stroke={primaryColor}
                            strokeWidth={2}
                            opacity={0.5}
                          />
                        )}

                        {/* Station name (show on hover or select) */}
                        {(isHovered || isSelected) && (
                          <g>
                            <rect
                              x={pos.x + 15}
                              y={pos.y - 12}
                              width={station.name.length * 7 + 10}
                              height={24}
                              rx={4}
                              fill="white"
                              stroke="#ddd"
                            />
                            <text
                              x={pos.x + 20}
                              y={pos.y + 4}
                              fontSize={12}
                              fill="#333"
                              fontWeight={500}
                            >
                              {station.name}
                            </text>
                          </g>
                        )}
                      </g>
                    )
                  })}
                </svg>

                {/* Instructions overlay */}
                <div className="absolute bottom-4 left-4 bg-white/90 px-3 py-2 rounded-lg text-sm text-neutral-dark/70">
                  Scroll to zoom • Drag to pan • Click station for details
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80">
            {/* Selected Station Info */}
            {selectedStationData ? (
              <div className="card mb-4">
                <h3 className="font-semibold text-lg mb-2">{selectedStationData.name}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedStationData.lines.map(lineId => {
                    const line = lines.find(l => l.id === lineId)
                    return line ? (
                      <span
                        key={lineId}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${line.colorHex}20`, color: line.colorHex }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: line.colorHex }}
                        ></span>
                        {line.name}
                      </span>
                    ) : null
                  })}
                </div>
                {selectedStationData.isInterchange && (
                  <p className="text-sm text-metro-violet mb-3">🔄 Interchange Station</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/station/${selectedStationData.id}`)}
                    className="flex-1 btn-primary text-sm py-2"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => navigate(`/route?from=${selectedStationData.id}`)}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    Route From
                  </button>
                </div>
              </div>
            ) : (
              <div className="card mb-4 text-center py-8 text-neutral-dark/50">
                <span className="text-3xl mb-2 block">📍</span>
                <p>Click a station to see details</p>
              </div>
            )}

            {/* Line Legend */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Metro Lines</h2>
              <div className="space-y-2">
                {lines.map(line => (
                  <div key={line.id} className="flex items-center gap-3">
                    <span
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: line.colorHex }}
                    ></span>
                    <span className="text-sm flex-1">{line.name}</span>
                    <span className="text-xs text-neutral-dark/50">
                      {line.stations.length} stations
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MetroMap
